import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  T,
  useT,
  type TranslationKey,
  type TranslateFn,
} from "@/lib/translation";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Settings,
  Plus,
  Trash2,
  Info,
  Download,
  Mail,
  Smartphone,
  Compass,
} from "lucide-react";
import { useLocalizedNavigate } from "@/hooks/useLocalizedPath";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import HelpTooltip from "@/components/shared/HelpTooltip";
import { toast } from "sonner";

import {
  UserSettingsUpdate,
  getCountry,
  exportUserData,
  deleteAccount,
  updateUserSettings,
  clearLoggedInCookie,
  type Fiat,
} from "@/api/client";
import { useUserSettings } from "@/features/settings/hooks/useUserSettings";
import { useCurrencies } from "@/hooks/useCurrencies";
import { CurrencySelectItems } from "@/components/shared/CurrencySelectItems";
import GoogleSecurityCard from "@/components/settings/GoogleSecurityCard";
import SubscriptionCard from "@/components/settings/SubscriptionCard";
import WelcomeModal from "@/components/WelcomeModal";
import AnalysisWelcomeModal from "@/components/AnalysisWelcomeModal";

// Trans' own key typing resolves to `never` with this project's non-strict TS
// config, so keys are checked here against the English files and cast once.

// -----------------------------
// Types & Schema
// -----------------------------
// Built per render with t() so validation messages follow the active language.
// Where people without the app get it for phone notifications.
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=app.oitava";

const createSettingsSchema = (t: TranslateFn) => {
  const taxTierSchema = z.object({
    fromYears: z.number().int().min(0, t("settings:validation.fromMin")),
    toYears: z.number().int().min(0, t("settings:validation.toMin")).optional(), // omitted for last tier
    isLastTier: z.boolean().optional().default(false),
    rate: z
      .number()
      .min(0, t("settings:validation.rateMin"))
      .max(100, t("settings:validation.rateMax")),
  });

  return z
    .object({
      display_name: z.string().max(100).optional(),
      country: z.string().optional(),
      preferred_display_currency: z
        .string()
        .min(1, t("settings:validation.currencyRequired")),
      theme: z.string().min(1, t("settings:validation.themeRequired")),
      tax_rules: z.object({
        type: z.enum(["fixed", "tiered"]),
        fixed_rate: z.number().min(0).max(100).optional(),
        dividend_rate: z.number().min(0).max(100),
        tiers: z.array(taxTierSchema).optional().default([]),
      }),
    })
    .superRefine((data, ctx) => {
      const tr = data.tax_rules;
      if (tr.type === "fixed") {
        if (typeof tr.fixed_rate !== "number") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["tax_rules", "fixed_rate"],
            message: t("settings:validation.fixedRateRequired"),
          });
        }
        if (tr.tiers && tr.tiers.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["tax_rules", "tiers"],
            message: t("settings:validation.tiersEmptyWhenFixed"),
          });
        }
      } else {
        const tiers = tr.tiers || [];
        if (tiers.length < 2) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["tax_rules", "tiers"],
            message: t("settings:validation.tieredMinRows"),
          });
          return;
        }
        const sorted = [...tiers].sort((a, b) => a.fromYears - b.fromYears);
        if (sorted[0].fromYears !== 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["tax_rules", "tiers", 0, "fromYears"],
            message: t("settings:validation.firstTierZero"),
          });
        }
        for (let i = 0; i < sorted.length; i++) {
          const cur = sorted[i];
          const isLast = i === sorted.length - 1;
          if (isLast) {
            if (!cur.isLastTier) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["tax_rules", "tiers", i, "isLastTier"],
                message: t("settings:validation.lastTierOpen"),
              });
            }
          } else {
            const next = sorted[i + 1];
            if (cur.isLastTier) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["tax_rules", "tiers", i, "isLastTier"],
                message: t("settings:validation.onlyLastOpen"),
              });
            }
            if (
              typeof cur.toYears !== "number" ||
              cur.toYears <= cur.fromYears
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["tax_rules", "tiers", i, "toYears"],
                message: t("settings:validation.toGreaterThanFrom"),
              });
            }
            if (
              typeof next.fromYears !== "number" ||
              next.fromYears !== cur.toYears
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["tax_rules", "tiers", i, "toYears"],
                message: t("settings:validation.consecutive"),
              });
            }
          }
        }
      }
    });
};

type SettingsFormData = z.infer<ReturnType<typeof createSettingsSchema>>;

type ApiTaxTier = {
  min_years: number;
  max_years?: number | null;
  rate: number;
};
type ApiTaxRules = {
  version: number;
  type: "fixed" | "tiered";
  capital_gains: {
    holding_period_unit: "year";
    rate?: number; // decimal fraction
    tiers?: ApiTaxTier[]; // decimal fractions
  };
  dividends: { rate: number }; // decimal fraction
};

// -----------------------------
// Constants
// -----------------------------
const COUNTRIES = [
  { value: "US", labelKey: "settings:countries.US" },
  { value: "AT", labelKey: "settings:countries.AT" },
  { value: "BE", labelKey: "settings:countries.BE" },
  { value: "CY", labelKey: "settings:countries.CY" },
  { value: "EE", labelKey: "settings:countries.EE" },
  { value: "FI", labelKey: "settings:countries.FI" },
  { value: "FR", labelKey: "settings:countries.FR" },
  { value: "DE", labelKey: "settings:countries.DE" },
  { value: "GR", labelKey: "settings:countries.GR" },
  { value: "IE", labelKey: "settings:countries.IE" },
  { value: "IT", labelKey: "settings:countries.IT" },
  { value: "LV", labelKey: "settings:countries.LV" },
  { value: "LT", labelKey: "settings:countries.LT" },
  { value: "LU", labelKey: "settings:countries.LU" },
  { value: "MT", labelKey: "settings:countries.MT" },
  { value: "NL", labelKey: "settings:countries.NL" },
  { value: "PL", labelKey: "settings:countries.PL" },
  { value: "PT", labelKey: "settings:countries.PT" },
  { value: "SK", labelKey: "settings:countries.SK" },
  { value: "SI", labelKey: "settings:countries.SI" },
  { value: "ES", labelKey: "settings:countries.ES" },
  { value: "AD", labelKey: "settings:countries.AD" },
  { value: "MC", labelKey: "settings:countries.MC" },
  { value: "SM", labelKey: "settings:countries.SM" },
  { value: "VA", labelKey: "settings:countries.VA" },
] as const satisfies readonly {
  value: string;
  labelKey: TranslationKey;
}[];

// -----------------------------
// Helpers: API <-> Form conversion
// -----------------------------
type FormTier = {
  fromYears: number;
  toYears?: number;
  isLastTier: boolean;
  rate: number;
};

function toPercent(x?: number | null): number {
  if (x == null) return 0;
  return Math.round(x * 100 * 100) / 100;
}
function toDecimal(x?: number | null): number {
  if (x == null) return 0;
  return Math.round((x / 100) * 1e6) / 1e6;
}

function apiTiersToForm(apiTiers?: ApiTaxTier[]): FormTier[] {
  if (!apiTiers || apiTiers.length === 0) return [];
  return apiTiers.map((t) => ({
    fromYears: t.min_years,
    toYears: t.max_years ?? undefined,
    isLastTier: t.max_years == null,
    rate: toPercent(t.rate),
  }));
}

function formTiersToApi(formTiers: FormTier[], t: TranslateFn): ApiTaxTier[] {
  const cleaned = [...(formTiers || [])]
    .map((t) => ({
      fromYears: Number(t.fromYears ?? 0),
      toYears: t.isLastTier ? undefined : Number(t.toYears ?? 0),
      isLastTier: !!t.isLastTier,
      rate: Number(t.rate ?? 0),
    }))
    .sort((a, b) => a.fromYears - b.fromYears);

  // Validate & normalize consecutiveness (also enforced by Zod)
  for (let i = 0; i < cleaned.length; i++) {
    const cur = cleaned[i];
    const next = cleaned[i + 1];
    if (
      !cur.isLastTier &&
      (cur.toYears == null || cur.toYears <= cur.fromYears)
    ) {
      throw new Error(t("settings:tierErrors.toGreaterThanFrom", { n: i + 1 }));
    }
    if (next && !cur.isLastTier && cur.toYears !== next.fromYears) {
      throw new Error(
        t("settings:tierErrors.consecutive", {
          n: i + 1,
          next: i + 2,
        }),
      );
    }
    if (cur.isLastTier && i !== cleaned.length - 1) {
      throw new Error(t("settings:tierErrors.onlyLastOpen"));
    }
  }

  return cleaned.map((t) => ({
    min_years: t.fromYears,
    ...(t.isLastTier ? {} : { max_years: t.toYears }),
    rate: toDecimal(t.rate),
  }));
}

function defaultTwoTiers(): FormTier[] {
  return [
    { fromYears: 0, toYears: 1, isLastTier: false, rate: 20 },
    { fromYears: 1, isLastTier: true, rate: 15 },
  ];
}

// -----------------------------
// Component
// TECH-DEBT: split candidate — SettingsModal is 1250+ lines and should be broken up:
//   - Lines 79-178  → settingsSchema.ts (Zod schema + types)
//   - Lines 200-241 → settingsConstants.ts (CURRENCIES, THEMES, COUNTRIES)
//   - Lines 244-313 → settingsTaxUtils.ts (apiTiersToForm, formTiersToApi, defaultTwoTiers)
//   - Lines 742-1063 (Personal tab JSX) → PersonalSettingsTab component
//   - Lines 1066-1507 (Tax tab JSX) → TaxSettingsTab component
//   - Lines 1509-1568 (Account tab JSX) → AccountSettingsTab component
// -----------------------------
export function SettingsModal({ children }: { children?: React.ReactNode }) {
  const t = useT();
  const settingsSchema = useMemo(() => createSettingsSchema(t), [t]);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const [countryHasNoDefaults, setCountryHasNoDefaults] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isTaxEditing, setIsTaxEditing] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [portfolioGuideOpen, setPortfolioGuideOpen] = useState(false);
  const [analysisGuideOpen, setAnalysisGuideOpen] = useState(false);
  const navigate = useLocalizedNavigate();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const { currencies } = useCurrencies();
  const lastTieredRef = useRef<FormTier[] | null>(null);
  const currencyTouchedRef = useRef(false);
  const taxTouchedRef = useRef(false);

  const form = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      display_name: "",
      country: "",
      preferred_display_currency: "",
      theme: "system",
      tax_rules: {
        type: "fixed",
        fixed_rate: 20,
        dividend_rate: 20,
        tiers: defaultTwoTiers(), // prepared; ignored when fixed
      },
    },
  });

  const { isDirty } = form.formState;

  const {
    fields: tierFields,
    insert,
    remove,
  } = useFieldArray({
    control: form.control,
    name: "tax_rules.tiers",
  });

  const { userSettings, isLoading, isUpdating, updateSettings } =
    useUserSettings(open);

  const { mutate: updateSettingsSilent } = useMutation({
    mutationFn: updateUserSettings,
    onMutate: async (newData) => {
      await queryClient.cancelQueries({ queryKey: ["userSettings"] });
      const previous = queryClient.getQueryData(["userSettings"]);
      // TECH-DEBT: any — `old as object` loses type; should use typed QueryData from getUserSettings return type
      queryClient.setQueryData(["userSettings"], (old: unknown) => ({
        ...(old as object),
        ...newData,
      }));
      return { previous };
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(["userSettings"], context?.previous);
    },
    onSettled: () =>
      queryClient
        .invalidateQueries({ queryKey: ["userSettings"] })
        .catch(() => {}),
  });

  const resetFormFromSettings = useCallback(() => {
    if (!userSettings) return;
    setCountryHasNoDefaults(false);
    const tr = userSettings.tax_rules as ApiTaxRules | undefined;
    const type = tr?.type || "fixed";
    const tiers = apiTiersToForm(tr?.capital_gains?.tiers);

    const ensuredTiers =
      type === "tiered" ? (tiers.length >= 2 ? tiers : defaultTwoTiers()) : []; // fixed => empty tiers

    // Keep snapshot only when tiered is loaded
    if (type === "tiered") {
      lastTieredRef.current = ensuredTiers;
    }

    currencyTouchedRef.current = false;
    taxTouchedRef.current = false;

    form.reset({
      display_name: userSettings.display_name || "",
      country: userSettings.country || "",
      preferred_display_currency: userSettings.preferred_display_currency,
      theme: userSettings.theme,
      tax_rules: {
        type,
        fixed_rate: toPercent(tr?.capital_gains?.rate ?? 0),
        dividend_rate: toPercent(tr?.dividends?.rate ?? 0),
        tiers: ensuredTiers,
      },
    });
  }, [userSettings, form]);

  useEffect(() => {
    resetFormFromSettings();
  }, [resetFormFromSettings]);

  // --- Focus-safe updates using setValue ---

  const handleToYearsChange = (index: number, raw: string) => {
    taxTouchedRef.current = true;
    const parsed = raw === "" ? undefined : Number.parseInt(raw, 10);
    const newTo =
      parsed === undefined || Number.isNaN(parsed) ? undefined : parsed;
    // set current.toYears
    form.setValue(`tax_rules.tiers.${index}.toYears`, newTo, {
      shouldValidate: true,
      shouldDirty: true,
    });

    // Update next.fromYears to keep consecutiveness (only if we have a numeric value)
    if (typeof newTo === "number" && index + 1 < tierFields.length) {
      form.setValue(`tax_rules.tiers.${index + 1}.fromYears`, newTo, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const handleRateChange = (index: number, raw: string) => {
    taxTouchedRef.current = true;
    const parsed = raw === "" ? undefined : Number.parseFloat(raw);
    const nextValue =
      typeof parsed === "number" && !Number.isNaN(parsed) ? parsed : undefined;
    // TECH-DEBT: any — `nextValue as number` is unsafe; nextValue can be undefined.
    // react-hook-form accepts undefined here but the type cast hides that.
    form.setValue(`tax_rules.tiers.${index}.rate`, nextValue as number, {
      shouldDirty: true,
    });
  };

  // Add a tier before the last (open-ended) row
  const handleAddTier = () => {
    taxTouchedRef.current = true;
    const tiers = form.getValues("tax_rules.tiers") || [];
    if (tiers.length < 1) {
      form.setValue("tax_rules.tiers", defaultTwoTiers(), {
        shouldValidate: true,
      });
      return;
    }
    const lastIdx = tiers.length - 1;
    const prev = tiers[lastIdx - 1] ?? tiers[0];
    const prevFrom = typeof prev.fromYears === "number" ? prev.fromYears : 0;
    const newFrom =
      typeof prev.toYears === "number" ? prev.toYears : prevFrom + 1;
    const newTo = newFrom + 1;

    insert(lastIdx, {
      fromYears: newFrom,
      toYears: newTo,
      isLastTier: false,
      rate: prev.rate,
    });

    // Ensure the (new) last row starts at newTo
    const lastRowIndex = lastIdx + 1; // last row shifted by insert
    form.setValue(`tax_rules.tiers.${lastRowIndex}.fromYears`, newTo, {
      shouldValidate: true,
    });
    form.setValue(`tax_rules.tiers.${lastRowIndex}.isLastTier`, true, {
      shouldValidate: true,
    });
    form.setValue(`tax_rules.tiers.${lastRowIndex}.toYears`, undefined, {
      shouldValidate: true,
    });
  };

  const handleRemoveTier = (index: number) => {
    taxTouchedRef.current = true;
    const tiers = form.getValues("tax_rules.tiers") || [];
    if (index === 0 || index === tiers.length - 1) return; // cannot remove first or last
    const prev = tiers[index - 1];
    const next = tiers[index + 1];

    remove(index);

    // After removal, "next" shifts to current index
    if (next && typeof prev?.toYears === "number") {
      form.setValue(`tax_rules.tiers.${index}.fromYears`, prev.toYears, {
        shouldValidate: true,
      });
    }
  };

  const handleTypeChange = (newType: "fixed" | "tiered") => {
    form.setValue("tax_rules.type", newType, {
      shouldValidate: true,
      shouldDirty: true,
    });

    if (newType === "fixed") {
      // Save current tiers so we can restore later
      const currentTiers = (form.getValues("tax_rules.tiers") ||
        []) as FormTier[];
      lastTieredRef.current = currentTiers;
      // Zod requires tiers empty in fixed mode
      form.setValue("tax_rules.tiers", [], {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else {
      // Restore previous tiered config if available; otherwise seed defaults
      const prev = lastTieredRef.current;
      const restored =
        Array.isArray(prev) && prev.length >= 2 ? prev : defaultTwoTiers();

      form.setValue("tax_rules.tiers", restored, {
        shouldValidate: true,
        shouldDirty: true,
      });

      // Ensure the last row is open-ended
      const lastIdx = restored.length - 1;
      form.setValue(`tax_rules.tiers.${lastIdx}.isLastTier`, true, {
        shouldValidate: true,
      });
      form.setValue(`tax_rules.tiers.${lastIdx}.toYears`, undefined, {
        shouldValidate: true,
      });
    }
  };

  const applyCountryTaxDefaults = async (countryCode: string) => {
    try {
      const c = await getCountry(countryCode);
      const rules = c?.default_tax_rules;
      if (rules) {
        setCountryHasNoDefaults(false);
        form.clearErrors(["tax_rules.dividend_rate", "tax_rules.fixed_rate"]);
        form.setValue(
          "tax_rules.dividend_rate",
          toPercent(rules.dividends?.rate ?? 0),
          { shouldDirty: true, shouldValidate: true },
        );
        const cgRateValue = rules.capital_gains?.rate;
        const cgRate =
          cgRateValue === undefined || cgRateValue === null
            ? undefined
            : Number(cgRateValue);
        if (
          rules.type === "fixed" &&
          cgRate !== undefined &&
          Number.isFinite(cgRate)
        ) {
          handleTypeChange("fixed");
          form.setValue("tax_rules.fixed_rate", toPercent(cgRate), {
            shouldDirty: true,
            shouldValidate: true,
          });
        } else if (
          rules.type === "tiered" &&
          Array.isArray(rules.capital_gains?.tiers) &&
          rules.capital_gains.tiers.length >= 2
        ) {
          handleTypeChange("tiered");
          const formTiers = apiTiersToForm(rules.capital_gains.tiers);
          lastTieredRef.current = formTiers;
          form.setValue("tax_rules.tiers", formTiers, {
            shouldDirty: true,
            shouldValidate: true,
          });
          const lastIdx = formTiers.length - 1;
          form.setValue(`tax_rules.tiers.${lastIdx}.isLastTier`, true, {
            shouldValidate: true,
          });
          form.setValue(`tax_rules.tiers.${lastIdx}.toYears`, undefined, {
            shouldValidate: true,
          });
        }
        taxTouchedRef.current = false;
      } else {
        setCountryHasNoDefaults(true);
        form.setValue("tax_rules.type", "fixed", {
          shouldDirty: true,
          shouldValidate: false,
        });
        form.setValue("tax_rules.tiers", [], {
          shouldDirty: true,
          shouldValidate: false,
        });
        form.setValue("tax_rules.dividend_rate", 0, {
          shouldDirty: true,
          shouldValidate: true,
        });
        form.setValue("tax_rules.fixed_rate", 0, {
          shouldDirty: true,
          shouldValidate: true,
        });
        lastTieredRef.current = defaultTwoTiers();
        form.clearErrors(["tax_rules.dividend_rate", "tax_rules.fixed_rate"]);
        taxTouchedRef.current = false;
      }
    } catch {
      /* ignore */
    }
  };

  const handleResetToDefaults = async () => {
    const country = form.getValues("country");
    if (!country) return;
    await applyCountryTaxDefaults(country);
    taxTouchedRef.current = false;
    setIsTaxEditing(false);
  };

  const onSubmit = (values: SettingsFormData) => {
    try {
      const payload: UserSettingsUpdate = {
        display_name: values.display_name || null,
        country: values.country || null,
        preferred_display_currency: values.preferred_display_currency,
        theme: values.theme,
        tax_rules: {
          version: 1,
          type: values.tax_rules.type,
          capital_gains:
            values.tax_rules.type === "fixed"
              ? {
                  holding_period_unit: "year",
                  rate: toDecimal(values.tax_rules.fixed_rate ?? 0),
                }
              : {
                  holding_period_unit: "year",
                  tiers: formTiersToApi(
                    (values.tax_rules.tiers || []) as FormTier[],
                    t,
                  ),
                },
          dividends: { rate: toDecimal(values.tax_rules.dividend_rate ?? 0) },
        } as ApiTaxRules,
      };
      updateSettings(payload, {
        onSuccess: () => {
          setIsTaxEditing(false);
        },
      });
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : t("settings:toast.fixTiers");
      toast.error(t("settings:toast.validationIssue"), {
        description: message,
      });
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportUserData();
    } catch {
      toast.error(t("settings:toast.exportFailed"));
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await deleteAccount();
      posthog?.reset();
      clearLoggedInCookie();
      queryClient.clear();
      navigate("/", { replace: true });
    } catch {
      toast.error(t("settings:toast.deleteFailed"));
      setIsDeleting(false);
    }
  };

  const currentType = form.watch("tax_rules.type");
  const watchedTiers = form.watch("tax_rules.tiers");
  const watchedDividendRate = form.watch("tax_rules.dividend_rate");
  const watchedFixedRate = form.watch("tax_rules.fixed_rate");

  useEffect(() => {
    if (currentType === "tiered" && Array.isArray(watchedTiers)) {
      lastTieredRef.current = watchedTiers as FormTier[];
    }
  }, [currentType, watchedTiers]);

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v && isDirty) {
            setShowDiscardDialog(true);
            return;
          }
          setOpen(v);
          if (!v) setIsTaxEditing(false);
        }}
      >
        <DialogTrigger asChild>
          {children ?? (
            <Button variant="outline" size="sm">
              <Settings className="h-4 w-4 mr-2" />
              {t("settings:trigger")}
            </Button>
          )}
        </DialogTrigger>

        <DialogContent
          aria-describedby={undefined}
          className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          <DialogHeader>
            <DialogTitle>{t("settings:title")}</DialogTitle>
          </DialogHeader>

          <Tabs
            defaultValue="personal"
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full flex-1 flex flex-col min-h-0 overflow-hidden"
          >
            <TabsList className="grid w-full grid-cols-3 mb-4">
              <TabsTrigger value="personal">
                {t("settings:tabs.personal")}
              </TabsTrigger>
              <TabsTrigger value="taxes">
                {t("settings:tabs.taxes")}
              </TabsTrigger>
              <TabsTrigger value="account">
                {t("settings:tabs.account")}
              </TabsTrigger>
            </TabsList>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex flex-col flex-1 min-h-0 overflow-hidden gap-6"
              >
                <div className="grid [&>*]:col-start-1 [&>*]:row-start-1 flex-1 min-h-0 overflow-hidden">
                  {/* PERSONAL TAB */}
                  <TabsContent
                    forceMount
                    value="personal"
                    className={
                      "space-y-4 !block overflow-y-auto h-full pb-2" +
                      (activeTab !== "personal"
                        ? " invisible pointer-events-none"
                        : "")
                    }
                  >
                    <Card>
                      <CardHeader>
                        <CardTitle>{t("settings:personal.title")}</CardTitle>
                        <CardDescription>
                          {t("settings:personal.description")}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="display_name"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t("settings:personal.nameLabel")}
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t(
                                      "settings:personal.namePlaceholder",
                                    )}
                                    maxLength={100}
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormItem>
                            <FormLabel>
                              {t("settings:personal.emailLabel")}
                            </FormLabel>
                            <FormControl>
                              <Input
                                value={userSettings?.email || ""}
                                readOnly
                                className="bg-muted/50 cursor-not-allowed"
                              />
                            </FormControl>
                          </FormItem>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="country"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t("settings:personal.countryLabel")}
                                </FormLabel>
                                <Select
                                  onValueChange={async (val) => {
                                    field.onChange(val);

                                    // 1) Currency: only auto-set if user hasn't picked a currency manually
                                    if (!currencyTouchedRef.current) {
                                      // Prefer backend currency if available
                                      try {
                                        const c = await getCountry(
                                          val as string,
                                        );
                                        const maybeFiat = c?.default_currency;
                                        if (
                                          currencies.some(
                                            (cur) => cur.code === maybeFiat,
                                          )
                                        ) {
                                          form.setValue(
                                            "preferred_display_currency",
                                            maybeFiat as Fiat,
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            },
                                          );
                                        }
                                      } catch {
                                        // ignore
                                      }
                                    }

                                    // 2) Tax defaults: only auto-apply if user hasn't edited tax this session
                                    if (!taxTouchedRef.current) {
                                      await applyCountryTaxDefaults(
                                        val as string,
                                      );
                                    }
                                  }}
                                  value={field.value || ""} // <- keep this one
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue
                                        placeholder={t(
                                          "settings:personal.countryPlaceholder",
                                        )}
                                      />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {COUNTRIES.map((c) => (
                                      <SelectItem key={c.value} value={c.value}>
                                        {t(c.labelKey)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="preferred_display_currency"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t("settings:personal.currencyLabel")}
                                </FormLabel>
                                <Select
                                  onValueChange={(val) => {
                                    currencyTouchedRef.current = true;
                                    field.onChange(val);
                                  }}
                                  value={field.value}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue
                                        placeholder={t(
                                          "settings:personal.currencyPlaceholder",
                                        )}
                                      />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <CurrencySelectItems variant="code-dash-label" />
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </CardContent>
                    </Card>

                    {/* Guides */}
                    <Card>
                      <CardHeader>
                        <CardTitle>{t("settings:guides.title")}</CardTitle>
                        <CardDescription>
                          {t("settings:guides.description")}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setPortfolioGuideOpen(true)}
                          >
                            <Compass className="h-4 w-4 mr-2" />
                            {t("settings:guides.portfolioGuide")}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setAnalysisGuideOpen(true)}
                          >
                            <Compass className="h-4 w-4 mr-2" />
                            {t("settings:guides.analysisGuide")}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Notifications */}
                    <Card>
                      <CardHeader>
                        <CardTitle>
                          {t("settings:notifications.title")}
                        </CardTitle>
                        <CardDescription>
                          {t("settings:notifications.description")}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-5">
                        {/* Email group */}
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              {t("settings:notifications.email")}
                            </p>
                          </div>
                          <div className="space-y-3 pl-1">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <p className="text-sm font-medium leading-none">
                                  {t("settings:notifications.weeklyDigest")}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {t(
                                    "settings:notifications.emailWeeklyDigestDescription",
                                  )}
                                </p>
                              </div>
                              <Switch
                                checked={
                                  userSettings?.email_weekly_earnings ?? false
                                }
                                onCheckedChange={(checked) =>
                                  updateSettingsSilent({
                                    email_weekly_earnings: checked,
                                  })
                                }
                              />
                            </div>
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <p className="text-sm font-medium leading-none">
                                  {t(
                                    "settings:notifications.transcriptAvailable",
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {t(
                                    "settings:notifications.emailTranscriptDescription",
                                  )}
                                </p>
                              </div>
                              <Switch
                                checked={
                                  userSettings?.email_transcript_ready ?? false
                                }
                                onCheckedChange={(checked) =>
                                  updateSettingsSilent({
                                    email_transcript_ready: checked,
                                  })
                                }
                              />
                            </div>
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <p className="text-sm font-medium leading-none">
                                  {t("settings:notifications.productUpdates")}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {t(
                                    "settings:notifications.productUpdatesDescription",
                                  )}
                                </p>
                              </div>
                              <Switch
                                checked={
                                  userSettings?.email_product_updates ?? true
                                }
                                onCheckedChange={(checked) =>
                                  updateSettingsSilent({
                                    email_product_updates: checked,
                                  })
                                }
                              />
                            </div>
                          </div>
                        </div>

                        <div className="border-t" />

                        {/* Phone group: pushes to the Oitava app. The bell always gets everything. */}
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />
                            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              {t("settings:notifications.phone")}
                            </p>
                          </div>
                          {(userSettings?.phone_app ?? "none") === "none" ? (
                            <div className="pl-1">
                              <p className="text-xs text-muted-foreground">
                                {t("settings:notifications.getApp")}
                              </p>
                              <a
                                href={PLAY_STORE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-1 inline-block text-sm font-medium text-primary hover:underline"
                              >
                                {t("settings:notifications.getAppLink")}
                              </a>
                            </div>
                          ) : (
                            <div className="space-y-3 pl-1">
                              {userSettings?.phone_app ===
                                "notifications_off" && (
                                <p className="text-xs text-muted-foreground rounded-md bg-muted px-3 py-2">
                                  {t("settings:notifications.phoneOff")}
                                </p>
                              )}
                              <div className="flex items-start justify-between gap-4">
                                <div>
                                  <p className="text-sm font-medium leading-none">
                                    {t("settings:notifications.weeklyDigest")}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {t(
                                      "settings:notifications.phoneWeeklyDigestDescription",
                                    )}
                                  </p>
                                </div>
                                <Switch
                                  checked={
                                    userSettings?.push_weekly_earnings ?? true
                                  }
                                  onCheckedChange={(checked) =>
                                    updateSettingsSilent({
                                      push_weekly_earnings: checked,
                                    })
                                  }
                                />
                              </div>
                              <div className="flex items-start justify-between gap-4">
                                <div>
                                  <p className="text-sm font-medium leading-none">
                                    {t(
                                      "settings:notifications.transcriptAvailable",
                                    )}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {t(
                                      "settings:notifications.phoneTranscriptDescription",
                                    )}
                                  </p>
                                </div>
                                <Switch
                                  checked={
                                    userSettings?.push_transcript_ready ?? true
                                  }
                                  onCheckedChange={(checked) =>
                                    updateSettingsSilent({
                                      push_transcript_ready: checked,
                                    })
                                  }
                                />
                              </div>
                              <div className="flex items-start justify-between gap-4">
                                <div>
                                  <p className="text-sm font-medium leading-none">
                                    {t("settings:notifications.referralRewards")}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {t(
                                      "settings:notifications.phoneReferralDescription",
                                    )}
                                  </p>
                                </div>
                                <Switch
                                  checked={
                                    userSettings?.push_referral_rewards ?? true
                                  }
                                  onCheckedChange={(checked) =>
                                    updateSettingsSilent({
                                      push_referral_rewards: checked,
                                    })
                                  }
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* TAX TAB */}
                  <TabsContent
                    forceMount
                    value="taxes"
                    className={
                      "space-y-4 !block overflow-y-auto h-full" +
                      (activeTab !== "taxes"
                        ? " invisible pointer-events-none"
                        : "")
                    }
                  >
                    <Card>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex flex-col space-y-1.5">
                            <CardTitle className="flex items-center gap-1">
                              <span>{t("settings:tax.title")}</span>
                              <HelpTooltip
                                side="right"
                                trigger={
                                  <span className="relative top-[3px] inline-flex h-5 w-5 items-center justify-center text-muted-foreground cursor-help">
                                    <Info className="h-4 w-4" />
                                  </span>
                                }
                              >
                                <p>{t("settings:tax.helpDefaults")}</p>
                                <p>{t("settings:tax.helpProgressive")}</p>
                              </HelpTooltip>
                            </CardTitle>
                            <CardDescription>
                              {t("settings:tax.description")}
                            </CardDescription>
                          </div>
                          {isTaxEditing ? (
                            <div className="flex items-center gap-2">
                              {!countryHasNoDefaults && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={handleResetToDefaults}
                                >
                                  {t("settings:tax.reset")}
                                </Button>
                              )}
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setIsTaxEditing(false)}
                              >
                                {t("common:actions.back")}
                              </Button>
                            </div>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setIsTaxEditing(true);
                                taxTouchedRef.current = true;
                              }}
                            >
                              {t("settings:tax.customize")}
                            </Button>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {isTaxEditing && (
                          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3 text-sm text-amber-800 dark:text-amber-200">
                            <Info className="h-4 w-4 mt-0.5 shrink-0" />
                            <span>{t("settings:tax.customizeNotice")}</span>
                          </div>
                        )}
                        {countryHasNoDefaults && (
                          <p className="text-sm text-muted-foreground">
                            {t("settings:tax.noDefaults")}
                          </p>
                        )}

                        {!isTaxEditing ? (
                          <div className="space-y-4">
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">
                                {t("settings:tax.dividendRateLabel")}
                              </p>
                              <p className="mt-1 text-sm">
                                {watchedDividendRate ?? 0}%
                              </p>
                            </div>
                            {currentType === "fixed" ? (
                              <div>
                                <p className="text-sm font-medium text-muted-foreground">
                                  {t("settings:tax.capitalGainsFixed")}
                                </p>
                                <p className="mt-1 text-sm">
                                  {watchedFixedRate ?? 0}%
                                </p>
                              </div>
                            ) : (
                              <div>
                                <p className="text-sm font-medium text-muted-foreground">
                                  {t("settings:tax.capitalGainsTiered")}
                                </p>
                                <div className="mt-1 space-y-1">
                                  {Array.isArray(watchedTiers) &&
                                    watchedTiers.map((tier, idx) => {
                                      const isLast =
                                        idx === watchedTiers.length - 1;
                                      return (
                                        <p key={idx} className="text-sm">
                                          {isLast
                                            ? t("settings:tax.tierOpenEnded", {
                                                from: tier.fromYears ?? 0,
                                              })
                                            : t("settings:tax.tierRange", {
                                                from: tier.fromYears ?? 0,
                                                to: tier.toYears ?? 0,
                                              })}
                                          : {tier.rate ?? 0}%
                                        </p>
                                      );
                                    })}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            {/* TECH-DEBT: duplicated in tax_rules.fixed_rate field below — the number-input onChange/onBlur
                                 pattern (parse → NaN-guard → field.onChange) is identical in both fields.
                                 Extract to a shared handleNumericChange(field, value) helper. */}
                            {/* Dividend Rate */}
                            <FormField
                              control={form.control}
                              name="tax_rules.dividend_rate"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>
                                    {t("settings:tax.dividendRateInput")}
                                  </FormLabel>
                                  <FormControl>
                                    <Input
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="0.1"
                                      placeholder="20"
                                      value={field.value ?? ""}
                                      onChange={(e) => {
                                        taxTouchedRef.current = true;
                                        const raw = e.target.value;
                                        const parsed =
                                          raw === ""
                                            ? undefined
                                            : Number.parseFloat(raw);
                                        if (
                                          parsed === undefined ||
                                          Number.isNaN(parsed)
                                        ) {
                                          field.onChange(undefined);
                                        } else {
                                          field.onChange(parsed);
                                        }
                                      }}
                                      onBlur={(e) => {
                                        if (e.target.value === "")
                                          field.onChange(0);
                                      }}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            {/* Fixed vs Tiered + Add Tier aligned on the right */}
                            <FormField
                              control={form.control}
                              name="tax_rules.type"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>
                                    {t("settings:tax.methodLabel")}
                                  </FormLabel>
                                  <FormControl>
                                    <div className="flex items-center justify-between">
                                      <RadioGroup
                                        className="flex gap-6"
                                        value={field.value}
                                        onValueChange={(v) => {
                                          taxTouchedRef.current = true;
                                          field.onChange(v);
                                          handleTypeChange(
                                            v as "fixed" | "tiered",
                                          );
                                        }}
                                      >
                                        <div className="flex items-center space-x-2">
                                          <RadioGroupItem
                                            value="fixed"
                                            id="fixed"
                                          />
                                          <label
                                            htmlFor="fixed"
                                            className="text-sm"
                                          >
                                            {t("settings:tax.fixedRate")}
                                          </label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <RadioGroupItem
                                            value="tiered"
                                            id="tiered"
                                          />
                                          <label
                                            htmlFor="tiered"
                                            className="text-sm"
                                          >
                                            {t("settings:tax.tieredByHolding")}
                                          </label>
                                        </div>
                                      </RadioGroup>

                                      {currentType === "tiered" && (
                                        <Button
                                          type="button"
                                          variant="secondary"
                                          onClick={handleAddTier}
                                        >
                                          <Plus className="h-4 w-4 mr-1" />
                                          {t("settings:tax.addTier")}
                                        </Button>
                                      )}
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            {currentType === "fixed" ? (
                              <FormField
                                control={form.control}
                                name="tax_rules.fixed_rate"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      {t("settings:tax.capitalGainsRateInput")}
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                        placeholder="20"
                                        value={field.value ?? ""}
                                        onChange={(e) => {
                                          taxTouchedRef.current = true;
                                          const raw = e.target.value;
                                          const parsed =
                                            raw === ""
                                              ? undefined
                                              : Number.parseFloat(raw);
                                          if (
                                            parsed === undefined ||
                                            Number.isNaN(parsed)
                                          ) {
                                            field.onChange(undefined);
                                          } else {
                                            field.onChange(parsed);
                                          }
                                        }}
                                        onBlur={(e) => {
                                          if (e.target.value === "")
                                            field.onChange(0);
                                        }}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            ) : (
                              <div className="space-y-3">
                                <div className="grid grid-cols-12 gap-2 text-xs text-muted-foreground">
                                  <div className="col-span-3">
                                    {t("settings:tax.fromYears")}
                                  </div>
                                  <div className="col-span-3">
                                    {t("settings:tax.toYears")}
                                  </div>
                                  <div className="col-span-4">
                                    {t("settings:tax.rate")}
                                  </div>
                                  <div className="col-span-2" />
                                </div>

                                {tierFields.map((field, idx) => {
                                  const isFirst = idx === 0;
                                  const isLast = idx === tierFields.length - 1;

                                  return (
                                    <div
                                      key={field.id}
                                      className="grid grid-cols-12 gap-2 items-center"
                                    >
                                      {/* FROM */}
                                      <div className="col-span-3">
                                        <Input
                                          type="number"
                                          min={0}
                                          step="1"
                                          value={
                                            form.watch(
                                              `tax_rules.tiers.${idx}.fromYears`,
                                            ) ?? ""
                                          }
                                          onChange={(e) => {
                                            const valStr = e.target.value;
                                            const val =
                                              valStr === ""
                                                ? undefined
                                                : parseInt(valStr, 10);
                                            if (isFirst) {
                                              form.setValue(
                                                `tax_rules.tiers.${idx}.fromYears`,
                                                0,
                                                { shouldDirty: true },
                                              );
                                            } else {
                                              if (typeof val === "number") {
                                                form.setValue(
                                                  `tax_rules.tiers.${idx - 1}.toYears`,
                                                  val,
                                                  {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                  },
                                                );
                                              }
                                              form.setValue(
                                                `tax_rules.tiers.${idx}.fromYears`,
                                                (typeof val === "number"
                                                  ? val
                                                  : undefined) as number,
                                                { shouldDirty: true },
                                              );
                                            }
                                          }}
                                          disabled={isFirst}
                                        />
                                      </div>

                                      {/* TO (disabled for last row) */}
                                      <div className="col-span-3">
                                        {isLast ? (
                                          <div className="h-9 flex items-center rounded-md border border-input bg-muted/30 px-3 text-sm text-muted-foreground">
                                            ≥{" "}
                                            {form.watch(
                                              `tax_rules.tiers.${idx}.fromYears`,
                                            ) ?? 0}
                                          </div>
                                        ) : (
                                          <Input
                                            type="number"
                                            min={
                                              ((form.getValues(
                                                `tax_rules.tiers.${idx}.fromYears`,
                                              ) as number) ?? 0) + 1
                                            }
                                            step="1"
                                            value={
                                              form.watch(
                                                `tax_rules.tiers.${idx}.toYears`,
                                              ) ?? ""
                                            }
                                            onChange={(e) =>
                                              handleToYearsChange(
                                                idx,
                                                e.target.value,
                                              )
                                            }
                                          />
                                        )}
                                      </div>

                                      {/* RATE */}
                                      <div className="col-span-4">
                                        <Input
                                          type="number"
                                          min="0"
                                          max="100"
                                          step="0.1"
                                          value={
                                            form.watch(
                                              `tax_rules.tiers.${idx}.rate`,
                                            ) ?? ""
                                          }
                                          onChange={(e) =>
                                            handleRateChange(
                                              idx,
                                              e.target.value,
                                            )
                                          }
                                          onBlur={(e) => {
                                            if (e.target.value === "") {
                                              form.setValue(
                                                `tax_rules.tiers.${idx}.rate`,
                                                0,
                                                { shouldDirty: true },
                                              );
                                            }
                                          }}
                                        />
                                      </div>

                                      {/* Actions */}
                                      <div className="col-span-2 flex justify-end">
                                        {!isFirst && !isLast && (
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="icon"
                                            onClick={() =>
                                              handleRemoveTier(idx)
                                            }
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}

                                <div className="text-xs text-muted-foreground pt-2">
                                  {t("settings:tax.tiersHint")}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* ACCOUNT TAB */}
                  <TabsContent
                    forceMount
                    value="account"
                    className={
                      "space-y-4 !block overflow-y-auto h-full" +
                      (activeTab !== "account"
                        ? " invisible pointer-events-none"
                        : "")
                    }
                  >
                    <Card>
                      <CardHeader>
                        <CardTitle>{t("settings:account.dataTitle")}</CardTitle>
                        <CardDescription>
                          {t("settings:account.dataDescription")}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleExport}
                            disabled={isExporting}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            {isExporting
                              ? t("settings:account.preparing")
                              : t("settings:account.exportData")}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                            onClick={() => {
                              setDeleteConfirmText("");
                              setShowDeleteDialog(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t("settings:account.deleteAccount")}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    <GoogleSecurityCard
                      isGoogleLinked={userSettings?.is_google_linked ?? false}
                      isAppleLinked={userSettings?.is_apple_linked ?? false}
                      hasPassword={userSettings?.has_password ?? true}
                    />

                    <SubscriptionCard />
                  </TabsContent>
                </div>

                <div
                  className={
                    "flex justify-end space-x-2" +
                    (activeTab === "account"
                      ? " invisible pointer-events-none"
                      : "")
                  }
                >
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      resetFormFromSettings();
                      setIsTaxEditing(false);
                    }}
                    disabled={!isDirty || isUpdating}
                  >
                    {t("common:actions.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={!isDirty || isUpdating || isLoading}
                  >
                    {isUpdating
                      ? t("settings:form.saving")
                      : t("settings:form.save")}
                  </Button>
                </div>
              </form>
            </Form>
          </Tabs>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("settings:deleteDialog.title")}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                {t("settings:deleteDialog.warning")}
              </span>
              <span className="block">
                {t("settings:deleteDialog.subscriptionNote")}
              </span>
              <span className="block">
                <T
                  k="settings:deleteDialog.confirmPrompt"
                  components={{ strong: <strong /> }}
                />
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={t("settings:deleteDialog.placeholder")}
            maxLength={6}
            className="my-2"
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t("common:actions.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== "DELETE" || isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting
                ? t("settings:deleteDialog.deleting")
                : t("settings:account.deleteAccount")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("settings:discardDialog.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings:discardDialog.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("settings:discardDialog.keep")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                resetFormFromSettings();
                setIsTaxEditing(false);
                setOpen(false);
              }}
            >
              {t("settings:discardDialog.discard")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <WelcomeModal
        open={portfolioGuideOpen}
        onClose={() => setPortfolioGuideOpen(false)}
      />
      <AnalysisWelcomeModal
        open={analysisGuideOpen}
        onClose={() => setAnalysisGuideOpen(false)}
      />
    </>
  );
}
